import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, ArrowUpRight } from 'lucide-react';
import { Button } from '../ui/button';

/**
 * PaywallContentGate — soft paywall wrapper used to show a teaser of paid
 * content to lower-tier plans. Currently used to gate Researched Posts +
 * Inspiration tab content for **Profile Success (one-time)** users, nudging
 * them to upgrade to the Executive Thought Leadership monthly plan.
 *
 * Rendering:
 *   - Top ~50% of the child renders clean and readable.
 *   - Bottom ~50% is progressively blurred via a mask + backdrop-filter.
 *   - A pill-shaped "Unlock full …" upsell CTA floats over the blur.
 *   - Text selection + copy are disabled inside the gate (best-effort:
 *     ``user-select: none`` + ``onCopy`` handler).
 *
 * Props:
 *   - ``gated`` (bool)   → when false, children are rendered unchanged.
 *   - ``label`` (str)    → what's being unlocked (e.g. "researched posts").
 *   - ``testId`` (str)   → optional data-testid prefix for the wrapper.
 *
 * Notes on defensiveness:
 *   - Copy-block is a *deterrent*, not a security boundary. Motivated users
 *     can still read the DOM. That's fine — the goal is UX friction to
 *     drive upgrades, not DRM.
 *   - Server-side gating for regenerate / new-post actions already exists
 *     via billing_mod.enforce().
 */
export const PaywallContentGate = ({
  gated = false,
  children,
  label = 'this content',
  testId = 'paywall-gate',
}) => {
  const navigate = useNavigate();

  if (!gated) return children;

  const blockCopy = (e) => {
    try { e.preventDefault(); } catch (_) {}
    try { e.stopPropagation(); } catch (_) {}
  };

  return (
    <div
      className="relative overflow-hidden rounded-xl select-none"
      onCopy={blockCopy}
      onCut={blockCopy}
      onContextMenu={blockCopy}
      onDragStart={blockCopy}
      style={{
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
        userSelect: 'none',
      }}
      data-testid={testId}
      data-content-gated="true"
    >
      {children}
      {/* Blur-out mask over the lower ~50% of the gated content */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-0 right-0 bottom-0"
        style={{
          top: '48%',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          background:
            'linear-gradient(to bottom, hsl(var(--card) / 0) 0%, hsl(var(--card) / 0.55) 30%, hsl(var(--card) / 0.95) 90%)',
        }}
      />
      {/* Upsell CTA */}
      <div
        className="absolute inset-x-0 bottom-3 flex justify-center px-4"
        data-testid={`${testId}-upsell`}
      >
        <Button
          size="sm"
          onClick={() => navigate('/pricing')}
          className="h-9 rounded-full text-[12px] shadow-lg"
        >
          <Lock size={12} className="mr-1.5" aria-hidden="true" />
          Unlock full {label}
          <ArrowUpRight size={12} className="ml-1" aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
};

export default PaywallContentGate;
